/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from "@azure/msal-browser";

const productionConfig = {
    b2cUrl: "cocentricssoeu.b2clogin.com",
    tenantUrl: "cocentricssoeu.onmicrosoft.com",
    clientId: "72f49c82-a295-4bf2-a796-49ef63031d50",
    apiEndpoint: "https://sso-api.cocentric.ai/api/User/apps",
    connectEndpoint: "https://connect-api.cocentric.ai/v3.0"
}

const integrationConfig = {
    b2cUrl: "login.cocentric.ai",
    tenantUrl: "cocentricsso.onmicrosoft.com",
    clientId: "2c346382-7e9a-462d-b88d-3223c29d333b",
    apiEndpoint: "https://sso-api-int.cocentric.ai/api/User/apps",
    connectEndpoint: "https://connect-api-int.cocentric.ai/v3.0"
}

let config = {}

switch (window.location.hostname) {
    case 'sso-web.cocentric.ai':
        config = productionConfig
        break;
    case 'sso-web-int.cocentric.ai':
        config = integrationConfig;
        break;
    case 'ennismore-int.cocentric.ai':
        config = integrationConfig;
        config['company'] = 'ennismore'
        break;
    case 'ennismore.cocentric.ai':
        config = productionConfig;
        config['company'] = 'ennismore'
        break;
    case 'gleneagles.cocentric.ai':
        config = productionConfig;
        config['company'] = 'gleneagles'
        break;
    case 'bistrotpierre.cocentric.ai':
        config = productionConfig;
        config['company'] = 'bistrotpierre'
        break;
    default:
        config = integrationConfig;
        break;
}

export default config;

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
let auth = '';
if (config.company && config.company !== undefined && config.company !== "") {
    auth = `https://${config.b2cUrl}/${config.tenantUrl}/B2C_1A_SIGNIN_USERNAMEOREMAIL_${config.company}`
}
else {
    auth = `https://${config.b2cUrl}/${config.tenantUrl}/B2C_1A_SIGNIN_USERNAMEOREMAIL`
}

export const b2cPolicies = {
    names: {
        signUpSignIn: 'B2C_1A_SIGNIN_USERNAMEOREMAIL',
        forgotPassword: 'B2C_1A_PASSWORDRESET_USERNAMEOREMAIL',
        editProfile: 'B2C_1_profile-edit',
    },
    authorities: {
        signUpSignIn: {
            authority: auth,
        },
        forgotPassword: {
            authority: `https://${config.b2cUrl}/${config.tenantUrl}/B2C_1A_PASSWORDRESET_USERNAMEOREMAIL`,
        },
        editProfile: {
            authority: `https://${config.b2cUrl}/${config.tenantUrl}/B2C_1_profile-edit`,
        },
    },
    authorityDomain: config.b2cUrl,
};


/**
 * Configuration object to be passed to MSAL instance on creation. 
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md 
 */
export const msalConfig = {
    auth: {
        clientId: config.clientId, // This is the ONLY mandatory field that you need to supply.
        authority: b2cPolicies.authorities.signUpSignIn.authority, // Choose SUSI as your default authority.
        knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
        // redirectUri: '/', // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
        // postLogoutRedirectUri: '/', // Indicates the page to navigate after logout.
        navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
    },
    cache: {
        cacheLocation: 'sessionStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
        storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        return;
                    case LogLevel.Info:
                        console.info(message);
                        return;
                    case LogLevel.Verbose:
                        console.debug(message);
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        return;
                    default:
                        return;
                }
            },
        },
    },
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const protectedResources = {
    ssoApi: {
        endpoint: config.apiEndpoint,
        scopes: {
            read: [`https://${config.tenantUrl}/api/SSO.Read`],
            admin: [`https://${config.tenantUrl}/api/SSO.Admin`],
        },
    },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit: 
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
    scopes: [...protectedResources.ssoApi.scopes.read, ...protectedResources.ssoApi.scopes.admin],
};
